import { TwoUIProgressView, TwoUIProgressViewProps } from "@amx/ux-twoui-telemetry";
import { Shimmer as FluentShimmer } from "@fluentui/react";
import React from "react";

export interface ShimmerProgressViewProps extends TwoUIProgressViewProps {
    // require firstLoadView
    readonly firstLoadView: Exclude<TwoUIProgressViewProps["firstLoadView"], undefined>;
    readonly isOverflowHidden?: boolean;
}

/**
 * Component that combines ProgressView with Shimmer to provide animation when transitioning from the first-load state
 */
export const ShimmerProgressView: React.FC<ShimmerProgressViewProps> = (props) => {
    const loadedView = (children?: JSX.Element) =>
        children && <ShimmerContainer shimmerElements={props.firstLoadView} isLoaded={true} isOverflowHidden={props.isOverflowHidden} children={children} />;

    return (
        <TwoUIProgressView
            operationData={props.operationData}
            inertView={props.inertView}
            firstLoadView={<ShimmerContainer shimmerElements={props.firstLoadView} />}
            finishedView={loadedView(props.finishedView)!}
            failedView={loadedView(props.failedView)}
            updatingView={loadedView(props.updatingView)}
            updateFailedView={loadedView(props.updateFailedView)}
        />
    );
};

interface ShimmerContainerProps {
    /**
     * Elements group to render for multi-line complex loading components
     */
    readonly shimmerElements: React.ReactNode;
    /**
     * Controls when the loading component is swapped with actual data through an animated transition.
     * Default is set to `false`.
     */
    readonly isLoaded?: boolean;
    /**
     * Controls if the component hides it's scrollbar.
     * Default is set to `false`.
     */
    readonly isOverflowHidden?: boolean;
}

const ShimmerContainer: React.FC<ShimmerContainerProps> = ({ children, shimmerElements, isLoaded, isOverflowHidden = false }) => (
    <FluentShimmer
        styles={{
            root: { width: "100%", overflow: isOverflowHidden ? "hidden" : undefined },
            shimmerWrapper: { overflow: "visible" },
            shimmerGradient: { display: "none" }
        }}
        shimmerColors={{
            shimmerWave: "transparent",
            shimmer: "transparent",
        }}
        isDataLoaded={isLoaded}
        customElementsGroup={shimmerElements}
    >
        {isLoaded && children}
    </FluentShimmer>
);
